
import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref,
  onMounted,
  watch,
} from "vue";
import { TranslationService } from "@/core/services/TranslationService";
import { hideModal } from "@/core/helpers/dom";
import { useToast } from "vue-toastification";
import { Form, Field, ErrorMessage, FieldArray } from "vee-validate";
import * as Yup from "yup";
import { UserAPI } from "@/services/api/User.service";
import { ICreateUserModel } from "@/interfaces/iUser";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { IUserPhoneModel } from "@/interfaces/iCurrentUser";
// import VZMultiSelect from "@/components/modals/forms/VZMultiSelect.vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import VZPhone from "@/components/VZPhone.vue";
import moment from "moment-timezone";
import AddSiteModal from "../modal/AddSiteModal.vue";
import { getLookups } from "@/services/api/Lookup.service";
export default defineComponent({
  name: "create-user",
  emits: ["close", "close-model"],
  components: {
    Form,
    Field,
    ErrorMessage,
    FieldArray,
    // VZMultiSelect,
    Datepicker,
    VZPhone,
    AddSiteModal,
  },
  props: {
    languageOption: {
      type: Object as PropType<{ label: any; value: string }>,
      // required: true,
    },
    siteOption: {
      type: Array as () => Array<{ label: any; value: string }>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const message = t("pages.user.userAddedMessage");
    const submitButton = ref<HTMLButtonElement | null>(null);
    const userSiteModels = ref<any[]>([]);
    const phoneModel = ref<IUserPhoneModel[]>();
    const formRef = ref<null | HTMLFormElement>(null);
    const showSiteDialog = ref(false);
    const roleOptions = ref([]) as any;
    const siteTableData = ref([]) as any;
    const siteOptions = ref([]) as any;
    const showErrorSite = ref(false);
    const password = ref<string>("");
    const password_confirmation = ref<string>("");
    const passwordTrue = ref<boolean>(false);
    const confirmPasswordTrue = ref<boolean>(false);
    const siteIdsOptions = computed(() => {
      return (
        props.siteOption &&
        props.siteOption.map((item) => {
          return {
            label: item.label,
            value: item.value,
          };
        })
      );
    });
    watch(
      siteIdsOptions,
      (newVal, oldVal) => {
        siteOptions.value = newVal;
      },
      { deep: true }
    );
    onMounted(() => {
      siteOptions.value = siteIdsOptions.value;
      getLookups(`security/filter/ROLES`)
        .then((res) => {
          res.map((data) =>
            roleOptions.value.push({
              label: data.text,
              value: data.value,
            })
          );
        })
        .catch((err) => {
          console.error(err);
        });
    });

    const removeSite = (item, index) => {
      siteTableData.value.splice(index, 1);
      const siteIdsInTable = siteTableData.value.map((item) => item.id);

      const filteredSiteIdsOptions = siteIdsOptions.value.filter(
        (item) => !siteIdsInTable.includes(item.value)
      );
      siteOptions.value = filteredSiteIdsOptions;
    };

    const submitSiteData = (data) => {
      const roleName = roleOptions.value.find(
        (option) => option.value == data[0].roleId
      )?.label;
      const roleId = roleOptions.value.find(
        (option) => option.value == data[0].roleId
      )?.value;
      data.forEach((item) => {
        const matchedItem = siteIdsOptions.value.find(
          (elem) => elem.value === item.siteId
        );
        if (matchedItem) {
          siteTableData.value.push({
            siteName: matchedItem.label,
            roleName: roleName,
            status: "Active",
            id: matchedItem.value,
            roleId: roleId,
          });
        }
      });
      const siteIdsInTable = siteTableData.value.map((item) => item.id);

      const filteredSiteIdsOptions = siteIdsOptions.value.filter(
        (item) => !siteIdsInTable.includes(item.value)
      );
      siteOptions.value = filteredSiteIdsOptions;
      showSiteDialog.value = false;
    };
    const cloneUserModel = ref<ICreateUserModel>({
      firstName: "",
      lastName: "",
      status: "Active",
      email: "",
      phoneNumber: "",
      language: "",
      profilePicture: "",
      userSiteModels: [],
    });
    const statusOptions = [
      {
        label: t("common.active"),
        value: "Active",
      },
      {
        label: t("common.inactive"),
        value: "Inactive",
      },
    ];
    let login = Yup.object().shape({
      firstName: Yup.string()
        .nullable()
        .max(30, getMaxLengthErrorMessage(t("common.firstName"), 30))
        .required(t("common.firstName") + " " + t("common.isRequired"))
        .matches(/^(?!\s*$)[A-Za-z\s]+$/, t("validation.notStartWithNum")),
      email: Yup.string()
        .email()
        .max(100, getMaxLengthErrorMessage(t("common.email"), 100))
        .matches(
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
          t("validation.emailValidation")
        )
        .required(t("common.email") + " " + t("common.isRequired")),
      defaultPhone: Yup.string().label(t("common.defaultPhone")),
      birthday: Yup.string()
        .required(t("validation.birthday") + " " + t("common.isRequired"))
        .nullable()
        .test("valid-birthday", t("validation.birthday"), function (value) {
          if (!value) {
            return this.createError({
              path: this.path,
              message: "Birthday is required",
            });
          }
          const selectedDate = new Date(value);
          const minBirthdate = new Date();
          minBirthdate.setFullYear(minBirthdate.getFullYear() - 16);
          if (selectedDate > minBirthdate) {
            return false;
          }
          return true;
        }),

      startDate: Yup.date()
        .required(t("common.startDate") + " " + t("common.isRequired"))
        .nullable(),
      endDate: Yup.date()
        .nullable()
        .min(Yup.ref("startDate"), t("common.endDateValidation")),
      lastName: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.lastName"), 30))
        .required(t("common.lastName") + " " + t("common.isRequired"))
        .matches(/^(?!\s*$)[A-Za-z\s]+$/, t("validation.notStartWithNum"))
        .nullable(),
      password: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.password"), 30))
        .required("Password is required")
        .label(t("common.password"))
        .matches(
          /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/,
          t("validation.passwordValidation")
        ),
      password_confirmation: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("pages.user.confirmPassword"), 30))
        .oneOf([Yup.ref("password"), null], "Password does not match")
        .required(
          t("pages.user.confirmPassword") + " " + t("common.isRequired")
        ),
      phoneModel: Yup.array().of(
        Yup.object().shape({
          phoneNumber: Yup.string().label(t("pages.user.phoneNumber")),
        })
      ),
    });
    const format = computed(() => {
      return FormatMethods;
    });
    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(cloneUserModel.value.startDate).format(
        "YYYY-MM-DD"
      );
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });
    const initialValues = computed(() => {
      return {
        phoneModel: phoneModel?.value
          ? [...phoneModel?.value]
          : [
              {
                phoneNumber: null,
                type: "CALL",
                defaultPhone: true,
              },
            ],
      };
    });
    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "MM-dd-yyyy";
    });
    const cloneUser = async (values, { resetForm }) => {
      try {
        if (siteTableData.value.length < 1) {
          showErrorSite.value = true;
          return;
        }
        if (validationErrors.value.includes(true)) {
          return;
        }

        const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
        const startMoment = moment(values.startDate).startOf("day");
        const formattedStartDate = startMoment.format(dateFormat);
        values.startDate = formattedStartDate;

        if (values.endDate) {
          const endMoment = moment(values.endDate).endOf("day");
          const formattedEndDate = endMoment.format(dateFormat);
          values.endDate = formattedEndDate;
        }
        values.status = values.status.toUpperCase();
        // eslint-disable-next-line
        submitButton.value!.disabled = true;
        submitButton.value?.setAttribute("data-kt-indicator", "on");
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {
          site,
          password_confirmation,
          phoneNumber,
          defaultPhone,
          phoneModel,
          ...rest
        } = values;
        const userSiteModels =
          siteTableData.value?.map((item) => {
            return {
              site: item.id,
              roleId: item.roleId,
            };
          }) ?? [];
        const userPhoneModels = values?.phoneModel?.map((phone, index) => {
          return {
            phoneModel: {
              phoneNumber: phone?.phoneNumber?.replace(/[^\d+]/g, ""),
              type: phone.type,
              webId: phone.webId,
              defaultPhone: index == defaultPhone,
            },
          };
        });
        delete rest.status;
        await UserAPI.getInstance().createUser({
          ...rest,
          userSiteModels,
          language: "",
          userPhoneModels,
          // phoneNumber: phoneNumber.replace(/[^A-Z0-9]/gi, ""),
        });
        emit("close");
        resetForm();
        hideCreateModal();
        toast.success(message);
      } finally {
        submitButton.value?.removeAttribute("data-kt-indicator");
        // eslint-disable-next-line
        submitButton.value!.disabled = false;
      }
    };
    const clearPassword = (e) => {
      // setTimeout(() => {
      //   e.target.value = "";
      // }, 100);
      passwordTrue.value = true;
    };
    const clearConfirmPassword = (e) => {
      // setTimeout(() => {
      //   e.target.value = "";
      // }, 100);
      confirmPasswordTrue.value = true;
    };
    //formRef?.value?.resetForm()
    const hideCreateModal = () => {
      emit("close-model");
      const cloneModal = document.querySelector(
        `#kt_modal_create_user_account`
      ) as HTMLElement;
      validationErrors.value = [];
      hideModal(cloneModal);
      cloneUserModel.value = {
        firstName: "",
        lastName: "",
        status: "Active",
        email: "",
        phoneNumber: "",
        language: "",
        profilePicture: "",
        userSiteModels: [],
      };
      formRef.value?.resetForm();
      nextTick(() => {
        formRef?.value?.resetForm();
      });
    };

    const validationErrors = ref([]) as any;
    const handleValidation = (value, index) => {
      validationErrors.value[index] = value;
    };
    const clearEmailField = (e) => {
      setTimeout(() => {
        e.target.value = "";
      }, 100);
    };
    return {
      t,
      cloneUser,
      login,
      submitButton,
      cloneUserModel,
      statusOptions,
      userSiteModels,
      format,
      phoneModel,
      initialValues,
      formRef,
      hideCreateModal,
      siteIdsOptions,
      displayDate,
      minDate,
      minEndDate,
      defaultPhoneIndex: 0,
      validationErrors,
      handleValidation,
      showSiteDialog,
      roleOptions,
      submitSiteData,
      siteTableData,
      removeSite,
      siteOptions,
      showErrorSite,
      clearEmailField,
      clearPassword,
      password,
      password_confirmation,
      passwordTrue,
      clearConfirmPassword,
      confirmPasswordTrue,
    };
  },
});
